import { create } from 'zustand';
import i18n from 'i18next';
import type { Task, CreateTaskRequest, UpdateTaskRequest, TaskPriority, TaskGroupCopy, Attachment } from '../types';
import { taskApi, attachmentApi } from '../services/api';
import { extractErrorResult } from '../utils/errorMessages';
import { useOrganizationStore } from './organizationStore';
import { useAuthStore } from './authStore';
import { showToast } from '../components/common';
import { websocketService } from '../services/websocket';

export interface TaskFilters {
  projectId?: string;
  projectIds?: string;
  assigneeId?: string;
  completed?: boolean;
  priority?: TaskPriority;
  dueDate?: string;
  dueDateFrom?: string;
  labels?: string[];
  search?: string;
  view?: string;
}

// Race condition prevention: Use a pending promise map for request deduplication
// This ensures that concurrent requests with the same parameters share a single promise
// rather than triggering multiple API calls
const pendingFetches = new Map<string, Promise<void>>();

// Real-time WebSocket subscription cleanup functions
const realtimeUnsubscribers: Array<() => void> = [];

// Generate a stable cache key from filters and pagination
// FIX: Include sortBy and sortOrder to prevent cache collisions when sorting changes
function getFetchCacheKey(filters: TaskFilters, page: number, limit: number, sortBy: string, sortOrder: 'asc' | 'desc'): string {
  return JSON.stringify({ ...filters, page, limit, sortBy, sortOrder });
}

interface TaskState {
  tasks: Task[];
  currentTask: Task | null;
  isLoading: boolean;
  error: string | null;
  filters: TaskFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
  historyTasks: Task[];
  historyPagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  // Global state for expanded task - only one can be open at a time
  expandedTaskId: string | null;
  setFilters: (filters: TaskFilters) => void;
  updateSearchAndFetch: (search: string | undefined) => void;
  clearFilters: () => void;
  clearTasks: () => void;
  clearCurrentTask: () => void;
  fetchTasks: (customFilters?: TaskFilters) => Promise<void>;
  fetchTasksHistory: (params?: Record<string, unknown>) => Promise<{ meta?: { page: number; limit: number; total: number; totalPages: number } } | void>;
  fetchTask: (id: string) => Promise<void>;
  createTask: (data: CreateTaskRequest) => Promise<Task | null>;
  updateTask: (id: string, data: UpdateTaskRequest) => Promise<boolean>;
  deleteTask: (id: string) => Promise<void>;
  toggleComplete: (id: string) => Promise<void>;
  assignToMe: (id: string) => Promise<boolean>;
  // Expand/collapse methods for dropdown system
  expandTask: (id: string) => void;
  collapseTask: () => void;
  toggleExpand: (id: string) => void;
  // Real-time WebSocket subscriptions
  subscribeToRealTimeUpdates: () => void;
  unsubscribeFromRealTimeUpdates: () => void;
  // Individual task group copies (creator's view of other members' copies)
  groupCopies: Record<string, TaskGroupCopy[]>;
  groupCopiesLoading: Record<string, boolean>;
  fetchGroupCopies: (taskId: string) => Promise<TaskGroupCopy[]>;
  // Attachment cache and operations
  attachments: Record<string, Attachment[]>;
  attachmentsLoading: Record<string, boolean>;
  fetchAttachments: (taskId: string) => Promise<Attachment[]>;
  deleteAttachment: (id: string, taskId?: string) => Promise<boolean>;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  currentTask: null,
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  historyTasks: [],
  historyPagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  // Initially no task is expanded
  expandedTaskId: null,
  // Group copies cache: taskId → copies array
  groupCopies: {},
  groupCopiesLoading: {},
  // Attachment cache: taskId → attachments array
  attachments: {},
  attachmentsLoading: {},

  // Expand a specific task - if another is open, close it first (only one can be open)
  expandTask: (id: string) => {
    set({ expandedTaskId: id });
  },

  // Collapse any currently expanded task
  collapseTask: () => {
    set({ expandedTaskId: null });
  },

  // Toggle expansion: if same task clicked, collapse; if different, expand (closing previous)
  toggleExpand: (id: string) => {
    const currentExpanded = get().expandedTaskId;
    if (currentExpanded === id) {
      set({ expandedTaskId: null });
    } else {
      set({ expandedTaskId: id });
    }
  },

  // Update filters state WITHOUT triggering a fetch (for programmatic use from pages)
  // Removes undefined values to keep the filter object clean
  setFilters: (filters) => {
    const currentFilters = get().filters;
    // Merge new filters, then remove any keys with undefined values
    const merged = { ...currentFilters, ...filters };
    const cleaned: TaskFilters = {};
    for (const [key, value] of Object.entries(merged)) {
      if (value !== undefined) {
        (cleaned as Record<string, unknown>)[key] = value;
      }
    }
    set({ filters: cleaned });
  },
  
  // Update search filter and trigger fetch (used by Header search)
  // This preserves existing filters (like view) while updating search
  updateSearchAndFetch: (search: string | undefined) => {
    const currentFilters = get().filters;
    // Remove search from filters if undefined, otherwise update it
    const { search: _, ...filtersWithoutSearch } = currentFilters;
    const newFilters = search 
      ? { ...filtersWithoutSearch, search } 
      : filtersWithoutSearch;
    set({ filters: newFilters });
    // Fetch with the updated filters
    get().fetchTasks(newFilters);
  },

  clearFilters: () => {
    set({ filters: {} });
  },

  clearTasks: () => {
    set({ tasks: [], error: null });
  },

  clearCurrentTask: () => {
    set({ currentTask: null });
  },

  fetchTasks: async (customFilters?: TaskFilters) => {
    // Use customFilters if provided, otherwise get from state
    const filtersToUse = customFilters ?? get().filters;
    const { pagination } = get();
    
    // Generate stable cache key for this request
    // FIX: Include sortBy and sortOrder to prevent cache collisions
    const cacheKey = getFetchCacheKey(filtersToUse, pagination.page, pagination.limit, pagination.sortBy, pagination.sortOrder);
    
    // CRITICAL FIX: Check for pending request with the same parameters
    // If one exists, return the same promise to prevent race conditions and duplicate calls
    const pendingFetch = pendingFetches.get(cacheKey);
    if (pendingFetch) {
      return pendingFetch;
    }
    
    // Set loading but DON'T clear tasks - keep showing stale data until new data arrives
    set({ isLoading: true, error: null });
    
    // Create the fetch promise and store it in the pending map
    const fetchPromise = (async () => {
      try {
        // FIX: Pass sortBy and sortOrder to API for proper server-side sorting
        // Include activeOrganizationId for multi-org support
        const activeOrgId = useOrganizationStore.getState().activeOrganizationId;
        const response = await taskApi.list({
          ...filtersToUse,
          organizationId: activeOrgId,
          page: pagination.page,
          limit: pagination.limit,
          sortBy: pagination.sortBy,
          sortOrder: pagination.sortOrder,
        });
        
        if (response.success && response.data) {
          // FIX REV-R4-5: Defense-in-depth — filter out other members' Individual copies
          // from the main task list. Even though the backend visibility filter should exclude
          // them, this ensures no Individual copy leaks into the inbox through any backend path.
          // Each user should only see their own assigned copy; others are in TaskGroupCopiesDropdown.
          const userId = useAuthStore.getState().user?.id;
          const filteredTasks = (response.data as Task[]).filter(task => {
            if (task.taskGroupId && !task.isGroupTask && task.assigneeId && task.assigneeId !== userId) {
              return false; // Skip — this is another member's Individual copy
            }
            return true;
          });
          set({ 
            tasks: filteredTasks, 
            isLoading: false,
            pagination: {
              page: response.meta?.page || 1,
              limit: response.meta?.limit || 20,
              total: response.meta?.total || 0,
              // FIX: Preserve sortBy and sortOrder from current state (not returned by API)
              sortBy: pagination.sortBy,
              sortOrder: pagination.sortOrder,
            },
          });
        } else {
          set({ error: response.error?.message || 'Failed to fetch tasks. Please try again.', isLoading: false });
        }
      } catch (error: unknown) {
        const { message } = extractErrorResult(error);
        set({ error: message, isLoading: false });
      } finally {
        // Always clean up the pending fetch entry
        pendingFetches.delete(cacheKey);
      }
    })();
    
    // Store the promise in the pending map for deduplication
    pendingFetches.set(cacheKey, fetchPromise);
    
    return fetchPromise;
  },

  fetchTasksHistory: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await taskApi.getHistory(params);
      if (response.success && response.data) {
        const meta = response.meta || { page: 1, limit: 20, total: 0 };
        set({ 
          historyTasks: response.data as Task[],
          historyPagination: {
            page: meta.page || 1,
            limit: meta.limit || 20,
            total: meta.total || 0,
            totalPages: meta.total ? Math.ceil(meta.total / (meta.limit || 20)) : 1,
          },
          isLoading: false,
        });
        return { meta: { page: meta.page || 1, limit: meta.limit || 20, total: meta.total || 0, totalPages: meta.total ? Math.ceil(meta.total / (meta.limit || 20)) : 1 } };
      } else {
        set({ error: response.error?.message || 'Failed to fetch task history. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  fetchTask: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await taskApi.get(id);
      if (response.success && response.data) {
        set({ currentTask: response.data as Task, isLoading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch task. Please try again.', isLoading: false });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
    }
  },

  createTask: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await taskApi.create(data as unknown as Record<string, unknown>);
      if (response.success && response.data) {
        const newTask = response.data as Task;
        const { tasks } = get();
        // Dedup: skip if already added via WebSocket event
        if (!tasks.some((t) => t.id === newTask.id)) {
          set({ tasks: [newTask, ...tasks], isLoading: false });
        } else {
          set({ isLoading: false });
        }
        return newTask;
      } else {
        set({ error: response.error?.message || 'Failed to create task. Please check your input and try again.', isLoading: false });
        return null;
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateTask: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await taskApi.update(id, data as unknown as Record<string, unknown>);
      if (response.success && response.data) {
        const updatedTask = response.data as Task;
        set({
          tasks: get().tasks.map((t) => (t.id === id ? updatedTask : t)),
          currentTask: get().currentTask?.id === id ? updatedTask : get().currentTask,
          isLoading: false,
        });
        return true; // FIX: Return success indicator for modal persistence logic
      } else {
        set({ error: response.error?.message || 'Failed to update task. Please try again.', isLoading: false });
        return false; // FIX: Return failure indicator
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message, isLoading: false });
      return false; // FIX: Return failure indicator
    }
  },

  deleteTask: async (id) => {
    // Optimistic update - remove task immediately from UI
    const previousTasks = get().tasks;
    set({
      tasks: previousTasks.filter((t) => t.id !== id),
      error: null,
    });
    
    try {
      const response = await taskApi.delete(id);
      // Delete returns 204 No Content, so response may be empty
      // Only revert if there's an explicit error
      if (response && response.success === false) {
        set({
          tasks: previousTasks,
          error: response.error?.message || 'Failed to delete task. Please try again.',
        });
      } else {
        showToast(i18n.t('tasks.store.taskDeleted'), 'success');
      }
    } catch (error: unknown) {
      // Revert on actual error (network error, 404, 500, etc.)
      const { message } = extractErrorResult(error);
      set({
        tasks: previousTasks,
        error: message,
      });
    }
  },

  toggleComplete: async (id) => {
    try {
      const response = await taskApi.complete(id);
      if (response.success && response.data) {
        const updatedTask = response.data as Task;
        set({
          tasks: get().tasks.map((t) => (t.id === id ? updatedTask : t)),
          currentTask: get().currentTask?.id === id ? updatedTask : get().currentTask,
        });
      }
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
    }
  },

  assignToMe: async (id) => {
    try {
      const response = await taskApi.assignToMe(id);
      if (response.success && response.data) {
        const updatedTask = response.data as Task;
        set({
          tasks: get().tasks.map((t) => (t.id === id ? updatedTask : t)),
          currentTask: get().currentTask?.id === id ? updatedTask : get().currentTask,
        });
        return true;
      }
      return false;
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      set({ error: message });
      return false;
    }
  },

  fetchGroupCopies: async (taskId: string) => {
    // Only fetch if not already cached
    const currentCopies = get().groupCopies[taskId];
    if (currentCopies) return currentCopies;

    set((state) => ({
      groupCopiesLoading: { ...state.groupCopiesLoading, [taskId]: true },
    }));

    try {
      const response = await taskApi.getGroupCopies(taskId);
      const copies: TaskGroupCopy[] = response.data ?? [];
      set((state) => ({
        groupCopies: { ...state.groupCopies, [taskId]: copies },
        groupCopiesLoading: { ...state.groupCopiesLoading, [taskId]: false },
      }));
      return copies;
    } catch (error: unknown) {
      set((state) => ({
        groupCopiesLoading: { ...state.groupCopiesLoading, [taskId]: false },
      }));
      return [];
    }
  },

  subscribeToRealTimeUpdates: () => {
    // Unsubscribe from any existing subscriptions first
    get().unsubscribeFromRealTimeUpdates();

    // task:created — prepend new task (dedup: skip if already exists)
    // Filter out Individual copies not assigned to the current user.
    // All users (creator AND non-creator) should only see their own copy in their task list.
    // The creator views other members' copies through the TaskGroupCopiesDropdown.
    // FIX [REV-NC1]: Previously only filtered for creator (creatorId === userId),
    // which let non-creator members see all other members' copies as separate tasks.
    realtimeUnsubscribers.push(
      websocketService.onTaskCreated((data) => {
        const { tasks } = get();
        const userId = useAuthStore.getState().user?.id;
        const newTask = data.task as Task;
        if (
          newTask.taskGroupId &&
          !newTask.isGroupTask &&
          newTask.assigneeId &&
          newTask.assigneeId !== userId
        ) {
          // Skip — this is another member's Individual copy
          // Each user should only see their own assigned copy in their list
          return;
        }
        if (!tasks.some((t) => t.id === data.task.id)) {
          set({ tasks: [data.task, ...tasks] });
        }
      })
    );

    // task:updated — replace matching task in array and currentTask
    // Also detect when an unassigned task is claimed by someone else
    // Also invalidate/update groupCopies cache if the updated task is a group copy
    // FIX [REV-NC2]: Defensive filter — skip updates for Individual copies not assigned to current user.
    // Even though .map() won't add new entries, this prevents stale updates on leaked copies.
    realtimeUnsubscribers.push(
      websocketService.onTaskUpdated((data) => {
        const userId = useAuthStore.getState().user?.id;
        const updatedTask = data.task as Task;
        // Defensive: skip updates for Individual copies not assigned to the current user
        if (
          updatedTask.taskGroupId &&
          !updatedTask.isGroupTask &&
          updatedTask.assigneeId &&
          updatedTask.assigneeId !== userId
        ) {
          return;
        }
        const currentTasks = get().tasks;
        const previousTask = currentTasks.find((t) => t.id === data.task.id);
        // Detect unassigned/group -> claimed transition by another user
        if (
          previousTask &&
          (previousTask.isUnassigned || previousTask.isGroupTask) &&
          !previousTask.assigneeId &&
          data.task.assigneeId &&
          data.task.assigneeId !== useAuthStore.getState().user?.id
        ) {
          showToast(i18n.t('tasks.card.someoneElseClaimed'), 'info');
        }
        // Update groupCopies cache: if the updated task has a taskGroupId, update it in cached copies
        if (updatedTask.taskGroupId) {
          set((state) => {
            const newCopies = { ...state.groupCopies };
            for (const [key, copies] of Object.entries(newCopies)) {
              const idx = copies.findIndex((c) => c.id === updatedTask.id);
              if (idx !== -1) {
                const updated: TaskGroupCopy[] = [...copies];
                updated[idx] = {
                  id: updatedTask.id,
                  title: updatedTask.title,
                  completed: updatedTask.completed,
                  priority: updatedTask.priority,
                  dueDate: updatedTask.dueDate ?? undefined,
                  assigneeId: updatedTask.assigneeId ?? undefined,
                  creatorId: updatedTask.creatorId,
                  completedById: updatedTask.completedById ?? undefined,
                  completedAt: updatedTask.completedAt ?? undefined,
                  createdAt: updatedTask.createdAt,
                  updatedAt: updatedTask.updatedAt,
                  assignee: updatedTask.assignee,
                  completedBy: updatedTask.completedBy,
                };
                newCopies[key] = updated;
              }
            }
            return { groupCopies: newCopies };
          });
        }
        set({
          tasks: currentTasks.map((t) => (t.id === data.task.id ? data.task : t)),
          currentTask: get().currentTask?.id === data.task.id ? data.task : get().currentTask,
        });
      })
    );

    // task:deleted — remove from array and clear currentTask if it matches
    // Also invalidate groupCopies cache entries that contain the deleted task
    realtimeUnsubscribers.push(
      websocketService.onTaskDeleted((data) => {
        // Always invalidate any cache entries containing the deleted task
        const deletedId = (data as any)?.task?.id || (data as any)?.id || (data as any)?.taskId;
        if (deletedId) {
          set((state) => {
            const newCopies = { ...state.groupCopies };
            for (const [key, copies] of Object.entries(newCopies)) {
              if (copies.some((c) => c.id === deletedId)) {
                delete newCopies[key];
              }
            }
            return { groupCopies: newCopies };
          });
        }
        set({
          tasks: get().tasks.filter((t) => t.id !== data.taskId),
          currentTask: get().currentTask?.id === data.taskId ? null : get().currentTask,
        });
      })
    );

    // task:completed — replace matching task in array and currentTask
    realtimeUnsubscribers.push(
      websocketService.onTaskCompleted((data) => {
        set({
          tasks: get().tasks.map((t) => (t.id === data.task.id ? data.task : t)),
          currentTask: get().currentTask?.id === data.task.id ? data.task : get().currentTask,
        });
      })
    );
  },

  unsubscribeFromRealTimeUpdates: () => {
    realtimeUnsubscribers.forEach((unsub) => unsub());
    realtimeUnsubscribers.length = 0;
  },

  fetchAttachments: async (taskId) => {
    const current = get().attachments[taskId];
    if (current) return current;

    set((state) => ({
      attachmentsLoading: { ...state.attachmentsLoading, [taskId]: true },
    }));

    try {
      const response = await attachmentApi.list(taskId);
      const attachments = response.data ?? [];
      set((state) => ({
        attachments: { ...state.attachments, [taskId]: attachments },
        attachmentsLoading: { ...state.attachmentsLoading, [taskId]: false },
        currentTask:
          state.currentTask?.id === taskId
            ? { ...state.currentTask, attachments }
            : state.currentTask,
      }));
      return attachments;
    } catch (error: unknown) {
      set((state) => ({
        attachmentsLoading: { ...state.attachmentsLoading, [taskId]: false },
      }));
      return [];
    }
  },

  deleteAttachment: async (id, taskId) => {
    try {
      const response = await attachmentApi.delete(id);
      if (response.success) {
        if (taskId) {
          set((state) => {
            const remaining = (state.attachments[taskId] ?? []).filter((a) => a.id !== id);
            return {
              attachments: { ...state.attachments, [taskId]: remaining },
              currentTask:
                state.currentTask?.id === taskId
                  ? { ...state.currentTask, attachments: remaining }
                  : state.currentTask,
              // Keep the task list in sync so TaskCard thumbnails update immediately
              tasks: state.tasks.map((t) =>
                t.id === taskId ? { ...t, attachments: remaining } : t
              ),
            };
          });
        }
        return true;
      }
      return false;
    } catch (error: unknown) {
      const { message } = extractErrorResult(error);
      showToast(message, 'error');
      return false;
    }
  },
}));
